import React, { useCallback, useEffect } from 'react';
import Modal from 'react-modal';
import { useNavigate } from 'react-router-dom';
import CommanModal from './CommanModal';

type SetState<T> = React.Dispatch<React.SetStateAction<T>>;

interface PendingCreditAlertToggle {
  alertId: string;
  type: string;
}

interface ManageAlertsModalProps {
  rb_manage_alerts: any;
  assets: any;

  showSuccessModal: boolean;
  setShowSuccessModal: SetState<boolean>;

  showVerificationPopup?: boolean;
  setShowVerificationPopup?: SetState<boolean>;

  showCreditAlertConfirmation?: boolean;
  setShowCreditAlertConfirmation?: SetState<boolean>;

  showUnsavedChangesModal: boolean;
  setShowUnsavedChangesModal: SetState<boolean>;
  unsavedChangesContext?: 'navigation' | 'form';

  showErrorModal?: boolean;
  setShowErrorModal?: SetState<boolean>;

  popupType?: 'sms' | 'email' | null;
  verificationPopupMessage?: string;
  errorMessage?: string;

  pendingCreditAlertToggle?: PendingCreditAlertToggle | null;
  setPendingCreditAlertToggle?: SetState<PendingCreditAlertToggle | null>;

  setSwitchStates?: SetState<{
    [key: string]: { email: boolean; sms: boolean };
  }>;

  setInputErrors?: SetState<Record<string, boolean>>;
  isAlertsModalOpen?: boolean;
  setIsAlertsModalOpen?: SetState<boolean>;
  showSuccessModalPhone?: boolean;
  setShowSuccessModalPhone?: SetState<boolean>;

  showErrorModalPhone?: boolean;
  setShowErrorModalPhone?: SetState<boolean>;

  // Functions to revert changes
  revertChanges?: () => void;

  // Function to unblock navigation after discarding changes
  unblockNavigation?: () => void;
  setShowPhoneVerificationModal?: SetState<boolean>;
  showPhoneVerificationModal?: boolean;
}

const ManageAlertsModals: React.FC<ManageAlertsModalProps> = ({
  rb_manage_alerts,
  assets,
  showSuccessModal,
  setShowSuccessModal,
  showVerificationPopup,
  setShowVerificationPopup,
  showCreditAlertConfirmation,
  setShowCreditAlertConfirmation,
  showUnsavedChangesModal,
  setShowUnsavedChangesModal,
  unsavedChangesContext = 'form',
  showErrorModal,
  setShowErrorModal,
  popupType,
  verificationPopupMessage,
  errorMessage,
  pendingCreditAlertToggle,
  setPendingCreditAlertToggle,
  setSwitchStates,
  setInputErrors,
  isAlertsModalOpen,
  setIsAlertsModalOpen,
  showSuccessModalPhone,
  setShowSuccessModalPhone,
  showErrorModalPhone,
  setShowErrorModalPhone,
  revertChanges,
  unblockNavigation,
  setShowPhoneVerificationModal,
  showPhoneVerificationModal,
}) => {
  const navigate = useNavigate();

  const confirmCreditAlertToggle = useCallback(() => {
    if (pendingCreditAlertToggle) {
      const { alertId, type } = pendingCreditAlertToggle;

      setSwitchStates?.((prev) => {
        const currentState = prev[alertId] || { email: false, sms: false };
        return {
          ...prev,
          [alertId]: {
            ...currentState,
            [type]: !currentState[type as keyof typeof currentState],
          },
        };
      });

      setInputErrors?.((prevErrors) => ({
        ...prevErrors,
        [alertId]: false,
      }));

      setPendingCreditAlertToggle?.(null);
      setShowCreditAlertConfirmation?.(false);
    }
  }, [
    pendingCreditAlertToggle,
    setSwitchStates,
    setInputErrors,
    setPendingCreditAlertToggle,
    setShowCreditAlertConfirmation,
  ]);

  const cancelCreditAlertToggle = () => {
    setPendingCreditAlertToggle?.(null);
    setShowCreditAlertConfirmation?.(false);
  };

  const handleOkClick = () => {
    setShowVerificationPopup?.(false);
  };

  const handleOkErrModalClick = () => {
    setShowErrorModal?.(false);
  };

  const handleErrorModalPhoneClick = () => {
    setShowErrorModalPhone?.(false);
  };

  const handleCancelUnSaved = () => {
    // Cancel: Close popup and keep the changes the user made (but don't save them)
    setShowUnsavedChangesModal(false);
  };

  const handleContinueUnSaved = () => {
    // Continue: Close popup and revert changes to initial state
    setShowUnsavedChangesModal(false);
    if (revertChanges) {
      revertChanges();
    }

    // Only allow navigation to proceed if this was triggered by navigation attempt
    if (unsavedChangesContext === 'navigation' && unblockNavigation) {
      unblockNavigation();
    }

    // Close the main alert preferences modal when Continue is clicked
    if (setIsAlertsModalOpen) {
      setIsAlertsModalOpen(false);
    }
  };

  const isAnyModalOpen =
    showVerificationPopup ||
    showCreditAlertConfirmation ||
    showUnsavedChangesModal ||
    showErrorModal ||
    showErrorModalPhone;

  const getTitle = () => {
    if (showVerificationPopup) {
      return popupType === 'sms'
        ? rb_manage_alerts?.manageAlertsContactsVerification?.verification_popup
            ?.title
        : rb_manage_alerts?.manageAlertsContactsVerification?.email_popup
            ?.title;
    } else if (showCreditAlertConfirmation) {
      return rb_manage_alerts?.manageAlertsCreditAlertConfirmation?.webTitle;
    } else if (showUnsavedChangesModal) {
      return rb_manage_alerts?.manageAlertsUnsavedPopup?.title;
    } else if (showErrorModalPhone || showErrorModal) {
      return rb_manage_alerts?.errorText;
    }
  };

  const getDescription = () => {
    if (showVerificationPopup) return verificationPopupMessage;
    if (showCreditAlertConfirmation)
      return rb_manage_alerts?.manageAlertsCreditAlertConfirmation
        ?.webDescription;
    if (showUnsavedChangesModal)
      return rb_manage_alerts?.manageAlertsUnsavedPopup?.description;
    if (showErrorModalPhone || showErrorModal)
      return rb_manage_alerts?.inconvenienceText;
  };
  const closephoneVerificationModal = () => {
    setShowPhoneVerificationModal?.(false);
    if (setIsAlertsModalOpen) {
      setIsAlertsModalOpen(false);
    }
  };

  const getOnCloseHandler = () => {
    if (showVerificationPopup) return handleOkClick;
    if (showCreditAlertConfirmation) return cancelCreditAlertToggle;
    if (showErrorModal) return handleOkErrModalClick;
    if (showErrorModalPhone) return handleErrorModalPhoneClick;
    return handleCancelUnSaved;
  };

  const getExtraButtonHandler = () => {
    if (showCreditAlertConfirmation) return confirmCreditAlertToggle;
    return handleContinueUnSaved;
  };

  useEffect(() => {
    if (showSuccessModalPhone) {
      const timer = setTimeout(() => {
        setShowSuccessModalPhone?.(false);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [showSuccessModalPhone, setShowSuccessModalPhone]);

  return (
    <>
      {(showSuccessModal || showSuccessModalPhone) && (
        <Modal
          isOpen={
            (showSuccessModal ?? false) || (showSuccessModalPhone ?? false)
          }
          onRequestClose={() => {
            if (showSuccessModal) setShowSuccessModal(false);
            if (showSuccessModalPhone) setShowSuccessModalPhone?.(false);
          }}
          contentLabel="requestCardSuccessModal"
          className="fixed p-0 w-[370px] top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 outline-none"
          overlayClassName="fixed inset-0 bg-black bg-opacity-50"
        >
          <div className="flex flex-col justify-center items-center gap-[20px] p-[20px] rounded-lg bg-white overflow-auto relative">
            <img
              src={assets?.device_recogonized_success_popup_icon}
              alt="successIcon"
              className="w-20 h-20"
            />
            <h3 className="text-[#1F1F1F] text-[22px] font-[650] leading-[28px] tracking-[0.077px] text-center">
              {showSuccessModal
                ? rb_manage_alerts?.webAlertsHeader?.successMsg
                : rb_manage_alerts?.webAlertsHeader?.webVerificationSent}
            </h3>
          </div>
        </Modal>
      )}

      {isAnyModalOpen && (
        <CommanModal
          isOpen={isAnyModalOpen}
          width={
            showCreditAlertConfirmation
              ? 'md:max-w-[480px] max-w-full'
              : undefined
          }
          height={
            showCreditAlertConfirmation ? 'md:h-auto h-[100vh]' : undefined
          }
          padding={
            showCreditAlertConfirmation ? 'px-6 pt-6 pb-0 md:pb-6' : undefined
          }
          jsxContent={
            <>
              <div className="mt-[20px] text-xl font-semibold text-center text-[#1F1F1F] leading-7">
                {getTitle()}
              </div>
              <div className="mt-[16px] text-[16px] text-[#1F1F1F] leading-6 text-center px-2">
                {getDescription()}
              </div>
            </>
          }
          buttonText={
            showVerificationPopup || showErrorModal || showErrorModalPhone
              ? rb_manage_alerts?.webAlertsHeader?.okCta
              : rb_manage_alerts?.manageAlertsCreditAlertConfirmation?.buttons
                  ?.cancel
          }
          cardStatus={true}
          onClose={getOnCloseHandler()}
          extraButtonText={
            showCreditAlertConfirmation
              ? rb_manage_alerts?.manageAlertsCreditAlertConfirmation?.buttons
                  ?.webContinue
              : showUnsavedChangesModal
                ? rb_manage_alerts?.manageAlertsUnsavedPopup?.buttons?.continue
                : undefined
          }
          onExtraButtonClick={getExtraButtonHandler()}
          closeIcon={false}
          buttonClassName={
            showCreditAlertConfirmation
              ? 'md:relative md:flex-row fixed bottom-0 w-full md:w-auto left-0 px-5 py-4 md:p-0 border-t border-t-[#E9E9E9] md:border-none bg-[#FAFAFA] md:bg-white gap-3 md:gap-4 mt-6'
              : undefined
          }
        />
      )}

      {showPhoneVerificationModal && (
        <CommanModal
          isOpen={showPhoneVerificationModal}
          width={
            showPhoneVerificationModal
              ? 'md:max-w-[400px] max-w-full'
              : undefined
          }
          height={
            showPhoneVerificationModal ? 'md:h-auto h-[100vh]' : undefined
          }
          padding={
            showPhoneVerificationModal ? 'px-6 pt-6 pb-0 md:pb-6' : undefined
          }
          jsxContent={
            <>
              <div className="mt-[20px] text-xl font-semibold text-center text-[#1F1F1F] leading-7">
                {rb_manage_alerts?.manageAlertsPhoneStatus?.smsVerificationModal
                  ?.title || 'Verify your phone number'}
              </div>
              <div className="mt-[16px] text-[16px] text-[#1F1F1F] leading-6 text-center px-2">
                {rb_manage_alerts?.manageAlertsPhoneStatus?.smsVerificationModal
                  ?.description ||
                  'To enable SMS alerts, you need to verify your phone number by replying to the SMS we sent you.'}
              </div>
            </>
          }
          buttonText={
            rb_manage_alerts?.manageAlertsPhoneStatus?.smsVerificationModal
              ?.ctaText || 'OK'
          }
          cardStatus={true}
          onClose={closephoneVerificationModal}
          closeIcon={false}
          buttonClassName={
            showCreditAlertConfirmation
              ? 'md:relative md:flex-row fixed bottom-0 w-full md:w-auto left-0 px-5 py-4 md:p-0 border-t border-t-[#E9E9E9] md:border-none bg-[#FAFAFA] md:bg-white gap-3 md:gap-4 mt-6'
              : undefined
          }
        />
      )}
    </>
  );
};

export default ManageAlertsModals;
